import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
// import { AuthContext } from '../../../contexts/AuthContext';
import { 
  DocumentChartBarIcon, 
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  MapIcon
} from '@heroicons/react/24/outline';

// Import report components
import RevenueReport from './RevenueReport';
import BookingsReport from './BookingsReport';
import CustomersReport from './CustomersReport';
import ToursReport from './ToursReport';

const ReportsPage = () => {
  const navigate = useNavigate();
  // const { login } = React.useContext(AuthContext);
  const [selectedReport, setSelectedReport] = useState('revenue');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  
  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      console.error('Authentication required: No token or user found in localStorage');
      // Redirect to login page if not authenticated
      navigate('/admin/login');
      return;
    }
    
    // Log authentication status for debugging
    console.log('User authenticated, token available for API requests');
    
    // Check if token is expired
    try {
      const userData = JSON.parse(user);
      console.log('Authenticated as:', userData.name || userData.email);
      
      // Check token expiration
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiryDate = new Date(payload.exp * 1000);
          const isExpired = expiryDate < new Date();
          
          if (isExpired) {
            console.warn('Token is expired!');
            // Clear expired token
            localStorage.removeItem('token');
            // Inform user and redirect to login
            alert('Your session has expired. Please log in again.');
            navigate('/admin/login');
            return;
          }
        } catch (e) {
          console.error('Error parsing token:', e);
        }
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
    }
  }, [navigate]);
  
  // No longer using re-authentication with stored credentials for security reasons

  // Fetch report data
  const { data: reportData, isLoading, isError, refetch } = useQuery(
    ['adminReport', selectedReport, dateRange],
    async () => {
      // Get the token directly before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await api.get(`/api/v1/admin/reports/${selectedReport}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        }
      });
      return response.data;
    },
    {
      keepPreviousData: true,
      retry: 1,
      onError: (error) => {
        console.error(`Error fetching ${selectedReport} report:`, error);
        // Display a toast or notification to the user
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error(`Server responded with status code ${error.response.status}`);
          console.error(`Response data:`, error.response.data);
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received from server');
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error setting up the request:', error.message);
        }
      }
    }
  );

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply date filter
  const applyDateFilter = (e) => {
    e.preventDefault();
    refetch();
  };

  // Handle report type change
  const handleReportChange = (reportType) => {
    setSelectedReport(reportType);
  };

  // Export report
  const exportReport = async (format = 'pdf') => {
    try {
      // Get the token directly before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please log in again.');
        navigate('/admin/login');
        return;
      }
      
      const response = await api.get(`/api/v1/admin/reports/${selectedReport}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          format
        },
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedReport}_report_${dateRange.startDate}_to_${dateRange.endDate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(`Error exporting ${selectedReport} report:`, error);
      alert('Failed to export report. Please try again.');
    }
  };

  // Placeholder data for when API data is not available
  const placeholderRevenueData = {
    summary: {
      total_revenue: 125000,
      average_booking_value: 1250,
      highest_revenue_day: {
        date: '2023-03-15',
        amount: 12500
      },
      revenue_by_payment_method: [
        { method: 'Credit Card', amount: 75000 },
        { method: 'PayPal', amount: 25000 },
        { method: 'Bank Transfer', amount: 15000 },
        { method: 'M-Pesa', amount: 10000 }
      ]
    },
    daily_revenue: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(2023, 2, i + 1).toISOString().split('T')[0],
      amount: Math.floor(Math.random() * 5000) + 1000
    })),
    revenue_by_tour: [
      { tour_name: 'Maasai Mara Safari', amount: 45000 },
      { tour_name: 'Mount Kenya Trek', amount: 30000 },
      { tour_name: 'Diani Beach Getaway', amount: 25000 },
      { tour_name: 'Nairobi National Park', amount: 15000 },
      { tour_name: 'Lake Nakuru Tour', amount: 10000 }
    ]
  };

  const placeholderBookingsData = {
    summary: {
      total_bookings: 100,
      confirmed_bookings: 80,
      pending_bookings: 15,
      cancelled_bookings: 5,
      average_group_size: 3.5
    },
    bookings_by_status: [
      { status: 'Confirmed', count: 80 },
      { status: 'Pending', count: 15 },
      { status: 'Cancelled', count: 5 }
    ],
    bookings_by_tour: [
      { tour_name: 'Maasai Mara Safari', count: 35 },
      { tour_name: 'Mount Kenya Trek', count: 25 },
      { tour_name: 'Diani Beach Getaway', count: 20 },
      { tour_name: 'Nairobi National Park', count: 12 },
      { tour_name: 'Lake Nakuru Tour', count: 8 }
    ],
    daily_bookings: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(2023, 2, i + 1).toISOString().split('T')[0],
      count: Math.floor(Math.random() * 5) + 1
    }))
  };

  const placeholderCustomersData = {
    summary: {
      total_customers: 85,
      new_customers: 25,
      returning_customers: 60,
      average_bookings_per_customer: 1.18
    },
    customers_by_country: [
      { country: 'United States', count: 30 },
      { country: 'United Kingdom', count: 20 },
      { country: 'Germany', count: 15 },
      { country: 'Canada', count: 10 },
      { country: 'Australia', count: 5 },
      { country: 'Other', count: 5 }
    ],
    customers_by_age_group: [
      { age_group: '18-24', count: 10 },
      { age_group: '25-34', count: 25 },
      { age_group: '35-44', count: 30 },
      { age_group: '45-54', count: 15 },
      { age_group: '55+', count: 5 }
    ]
  };

  const placeholderToursData = {
    summary: {
      total_tours: 15,
      active_tours: 12,
      most_booked_tour: 'Maasai Mara Safari',
      least_booked_tour: 'Lake Turkana Adventure'
    },
    tours_by_bookings: [
      { tour_name: 'Maasai Mara Safari', bookings: 35 },
      { tour_name: 'Mount Kenya Trek', bookings: 25 },
      { tour_name: 'Diani Beach Getaway', bookings: 20 },
      { tour_name: 'Nairobi National Park', bookings: 12 },
      { tour_name: 'Lake Nakuru Tour', bookings: 8 }
    ],
    tours_by_revenue: [
      { tour_name: 'Maasai Mara Safari', revenue: 45000 },
      { tour_name: 'Mount Kenya Trek', revenue: 30000 },
      { tour_name: 'Diani Beach Getaway', revenue: 25000 },
      { tour_name: 'Nairobi National Park', revenue: 15000 },
      { tour_name: 'Lake Nakuru Tour', revenue: 10000 }
    ],
    tours_by_rating: [
      { tour_name: 'Maasai Mara Safari', rating: 4.8 },
      { tour_name: 'Mount Kenya Trek', rating: 4.7 },
      { tour_name: 'Diani Beach Getaway', rating: 4.9 },
      { tour_name: 'Nairobi National Park', rating: 4.5 },
      { tour_name: 'Lake Nakuru Tour', rating: 4.6 }
    ]
  };

  // Use placeholder data if API data is not available or if there's an error
  // If we have real data from the API, use it; otherwise, use placeholder data
  const reportDataToUse = isLoading || isError ? null : reportData;
  
  // Create a processed data object that will work with our components
  // If API data is available, use it; otherwise, use placeholder data
  const processedData = {
    data: reportDataToUse?.data || (
      selectedReport === 'revenue' ? placeholderRevenueData :
      selectedReport === 'bookings' ? placeholderBookingsData :
      selectedReport === 'customers' ? placeholderCustomersData :
      placeholderToursData
    )
  };

  // Report type options
  const reportTypes = [
    { id: 'revenue', name: 'Revenue Report', icon: CurrencyDollarIcon },
    { id: 'bookings', name: 'Bookings Report', icon: DocumentChartBarIcon },
    { id: 'customers', name: 'Customers Report', icon: UserGroupIcon },
    { id: 'tours', name: 'Tours Report', icon: MapIcon }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => exportReport('pdf')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
            Export PDF
          </button>
          <button
            onClick={() => exportReport('csv')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Report Type Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Report Type</h2>
              <nav className="space-y-1" aria-label="Report Types">
                {reportTypes.map((report) => (
                  <button
                    key={report.id}
                    onClick={() => handleReportChange(report.id)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full
                      ${selectedReport === report.id
                        ? 'bg-primary text-white'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}
                    `}
                    aria-current={selectedReport === report.id ? 'page' : undefined}
                  >
                    <report.icon
                      className={`
                        flex-shrink-0 -ml-1 mr-3 h-6 w-6
                        ${selectedReport === report.id
                          ? 'text-white'
                          : 'text-gray-400 group-hover:text-gray-500'}
                      `}
                      aria-hidden="true"
                    />
                    <span className="truncate">{report.name}</span>
                  </button>
                ))}
              </nav>

              {/* Date Range Filter */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Date Range</h3>
                <form onSubmit={applyDateFilter} className="space-y-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        value={dateRange.startDate}
                        onChange={handleDateRangeChange}
                        className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        value={dateRange.endDate}
                        onChange={handleDateRangeChange}
                        className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Apply Filter
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Report Content */}
        <div className="lg:col-span-3">
          <div className="bg-white shadow rounded-lg">
            {isLoading ? (
              <div className="animate-pulse p-6">
                <div className="h-10 bg-gray-200 rounded mb-4"></div>
                <div className="h-60 bg-gray-200 rounded mb-4"></div>
                <div className="h-40 bg-gray-200 rounded"></div>
              </div>
            ) : isError ? (
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-center text-gray-900 mb-2">Unable to load live report data</h3>
                <p className="text-sm text-gray-500 text-center mb-4">We're showing you placeholder data instead. You can try refreshing to load the latest data.</p>
                <div className="flex justify-center">
                  <button
                    onClick={() => refetch()}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Retry Loading Data
                  </button>
                </div>
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <p className="text-xs text-gray-500 text-center">Note: The report below contains placeholder data for demonstration purposes.</p>
                </div>
              </div>
            ) : (
              <div className="px-4 py-5 sm:p-6">
                {isError && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Showing placeholder data. Live data could not be loaded.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {selectedReport === 'revenue' && <RevenueReport data={processedData} dateRange={dateRange} />}
                {selectedReport === 'bookings' && <BookingsReport data={processedData} dateRange={dateRange} />}
                {selectedReport === 'customers' && <CustomersReport data={processedData} dateRange={dateRange} />}
                {selectedReport === 'tours' && <ToursReport data={processedData} dateRange={dateRange} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
